/**
 * @fileoverview Eligible tenant picker for the legacy migration page.
 * @module system-web/components/migration/EligibleTenantPicker
 *
 * Responsibilities:
 * - Render a list of eligible tenants (selectable), broken-link tenants (disabled),
 *   and a footer count of hidden tenants.
 * - Signal the selected tenant to the parent via onSelect callback.
 *
 * Dependencies:
 * - lucide-react (icons)
 * - useEligibleTenants hook
 *
 * Architecture:
 * - Reads from useEligibleTenants hook (local-first liveQuery on Dexie `tenants`).
 * - Purely presentational; does not call any API directly.
 *
 * Notes:
 * - Broken-link tenants are visible but greyed with tooltip (§3.8.3 of impl plan).
 * - All other ineligible reasons are hidden; footer count is surfaced.
 *
 * @reusable
 * Usage: Rendered in LegacyMigrationPage left column.
 * Rules: stateful (selected tenant highlight); receives data from parent via hook.
 */

import React, { useMemo, useState } from 'react';
import { CheckCircle2, AlertTriangle, Loader2, XCircle, Search, Check } from 'lucide-react';
import type { EligibleTenantEntry, BrokenLinkTenantEntry } from '../../hooks/useEligibleTenants';

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

export type TenantMigrationFilterStatus = 'all' | 'pending' | 'partial' | 'migrated' | 'in_progress' | 'stalled' | 'paused';
export type TenantMigrationCompleteness = 'pending' | 'partial' | 'migrated' | 'in_progress' | 'stalled' | 'paused';

/**
 * @contract EligibleTenantPickerProps
 *
 * Purpose:
 * - Props for the EligibleTenantPicker component.
 *
 * Fields:
 * - eligible: Tenants eligible for migration (from useEligibleTenants).
 * - brokenLink: Tenants with broken legacy linkage (visible but disabled).
 * - hiddenCount: Number of tenants hidden due to wrong status / not linked / etc.
 * - isLoading: True while liveQuery is initializing.
 * - error: Non-null when the subscription fails.
 * - selectedId: Currently-selected tenant _id.
 * - onSelect: Callback invoked when the user selects an eligible tenant.
 * - physicalByLogical: Optional map of logical legacy database key -> resolved physical
 *   database name for the current server environment.
 * - completenessByTenantId: Optional map of tenantId → pending | partial | migrated | in_progress | paused.
 *   in_progress and paused override Migrated. paused means the worker has not reported for 2 minutes.
 *
 * Rules:
 * - brokenLink tenants are rendered with a tooltip explaining the issue.
 * - onSelect is never called for broken-link or hidden tenants.
 *
 * Sync:
 * - eligible and brokenLink come from Dexie liveQuery (passed as props).
 */
export interface EligibleTenantPickerProps {
  eligible: EligibleTenantEntry[];
  brokenLink: BrokenLinkTenantEntry[];
  hiddenCount: number;
  isLoading: boolean;
  error: string | null;
  selectedId: string | null;
  onSelect: (entry: EligibleTenantEntry) => void;
  physicalByLogical?: Record<string, string | null>;
  migratedTenantIds?: Set<string> | string[];
  completenessByTenantId?: Record<string, TenantMigrationCompleteness>;
  appEnv?: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * @component EligibleTenantPicker
 *
 * Purpose:
 * - Render the department selection list with eligible / disabled / hidden-count buckets.
 *
 * Props:
 * - See EligibleTenantPickerProps.
 *
 * Behavior:
 * - Eligible rows: clickable, highlight on selection.
 * - Broken-link rows: greyed out with hover tooltip.
 * - Footer: shows hidden tenant count with split by reason summary.
 *
 * Data Source:
 * - local (Dexie via useEligibleTenants — passed as props)
 *
 * Constraints:
 * - No direct API calls.
 *
 * Performance:
 * - No virtualization needed (expected < 100 tenants).
 *
 * @reusable
 * Usage: LegacyMigrationPage left sidebar.
 * Rules: presentation-only; stateless beyond passed selectedId.
 */
export function EligibleTenantPicker({
  eligible,
  brokenLink,
  hiddenCount,
  isLoading,
  error,
  selectedId,
  onSelect,
  physicalByLogical,
  migratedTenantIds,
  completenessByTenantId,
  appEnv,
}: EligibleTenantPickerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<TenantMigrationFilterStatus>('all');

  const envBadge = appEnv
    ? appEnv.toLowerCase() === 'prod' || appEnv.toLowerCase() === 'production'
      ? 'Main'
      : appEnv.toLowerCase() === 'stage' || appEnv.toLowerCase() === 'staging'
      ? 'Stage'
      : 'Dev'
    : null;

  const completenessOf = (tenantId: string): TenantMigrationCompleteness => {
    if (completenessByTenantId?.[tenantId]) return completenessByTenantId[tenantId];
    if (migratedTenantIds) {
      const migratedSet = migratedTenantIds instanceof Set ? migratedTenantIds : new Set(migratedTenantIds);
      if (migratedSet.has(tenantId)) return 'migrated';
    }
    return 'pending';
  };

  const matchesSearch = (entry: EligibleTenantEntry | BrokenLinkTenantEntry, q: string) => {
    if (!q) return true;
    const nameMatch = entry.name.toLowerCase().includes(q);
    const deptIdMatch = 'legacyDeptId' in entry && entry.legacyDeptId != null ? String(entry.legacyDeptId).includes(q) : false;
    const deptCodeMatch = entry.legacyDeptCode ? entry.legacyDeptCode.toLowerCase().includes(q) : false;
    const tenantIdMatch = entry.legacyTenantId != null ? String(entry.legacyTenantId).includes(q) : false;
    return nameMatch || deptIdMatch || deptCodeMatch || tenantIdMatch;
  };

  const processedEligible = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = eligible.filter((entry) => matchesSearch(entry, q));

    if (filterStatus !== 'all') {
      list = list.filter((entry) => completenessOf(entry._id) === filterStatus);
    }

    const rank = (id: string) => {
      const status = completenessOf(id);
      if (status === 'paused') return 0;
      if (status === 'stalled') return 1;
      if (status === 'in_progress') return 2;
      if (status === 'pending') return 2;
      if (status === 'partial') return 3;
      return 4;
    };
    return list.sort((a, b) => {
      const diff = rank(a._id) - rank(b._id);
      if (diff !== 0) return diff;
      return a.name.localeCompare(b.name);
    });
  }, [eligible, searchQuery, filterStatus, completenessByTenantId, migratedTenantIds]);

  const processedBrokenLink = useMemo(() => {
    if (filterStatus === 'migrated') return [];
    const q = searchQuery.trim().toLowerCase();
    return brokenLink.filter((entry) => matchesSearch(entry, q));
  }, [brokenLink, searchQuery, filterStatus]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm opacity-60">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading departments…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-red-600">
        <XCircle className="h-4 w-4" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        <input
          type="text"
          placeholder="Search name, Dept ID, Tenant ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-8 pr-2.5 py-1 text-xs border border-gray-300 rounded-md focus:outline-hidden focus:ring-1 focus:ring-orange-500 bg-white"
        />
      </div>

      {/* Three-Way Filter Tabs */}
      <div className="flex rounded-md bg-gray-100 p-0.5 text-xs font-medium text-gray-600">
        {(['all', 'pending', 'partial', 'in_progress', 'stalled', 'paused', 'migrated'] as const).map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setFilterStatus(status)}
            className={`flex-1 py-1 px-1.5 text-center capitalize rounded transition-colors ${
              filterStatus === status ? 'bg-white text-orange-600 font-semibold shadow-xs' : 'hover:text-gray-900'
            }`}
          >
            {status === 'in_progress' ? 'Live' : status === 'stalled' ? 'Stalled' : status === 'paused' ? 'Paused' : status}
          </button>
        ))}
      </div>

      {processedEligible.length === 0 && processedBrokenLink.length === 0 ? (
        <div className="py-6 text-center text-sm opacity-50">
          <p>No departments match filter.</p>
          {hiddenCount > 0 && (
            <p className="mt-1 text-xs">{hiddenCount} department{hiddenCount > 1 ? 's' : ''} hidden (not linked to Streamline)</p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-0.5">
          {processedEligible.map((entry) => {
            const isSelected = entry._id === selectedId;
            const completeness = completenessOf(entry._id);

            return (
              <button
                key={entry._id}
                onClick={() => onSelect(entry)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-left text-sm transition-colors border"
                style={{
                  background: isSelected ? 'var(--primary-light, rgba(234,88,12,0.08))' : 'transparent',
                  borderColor: isSelected ? 'var(--primary, #ea580c)' : 'transparent',
                  color: isSelected ? 'var(--primary, #ea580c)' : 'inherit',
                }}
              >
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  {completeness === 'in_progress' || completeness === 'paused' || completeness === 'stalled'
                    ? <Loader2 className={`h-4 w-4 shrink-0 ${completeness === 'in_progress' ? 'text-orange-600 animate-spin' : 'text-amber-600'}`} />
                    : <CheckCircle2 className={`h-4 w-4 shrink-0 ${completeness === 'migrated' ? 'text-green-600' : completeness === 'partial' ? 'text-amber-500' : 'text-gray-400'}`} />}
                  <div className="min-w-0">
                    <p className="font-medium truncate">{entry.name}</p>
                    <p className="text-xs opacity-60 truncate">
                      {entry.legacyDb}
                      {physicalByLogical?.[entry.legacyDb] && (
                        <span className="font-mono text-blue-600"> → {physicalByLogical[entry.legacyDb]}</span>
                      )}
                      {' · Dept '}{entry.legacyDeptId}
                      {entry.legacyTenantId != null && ` · Tenant ${entry.legacyTenantId}`}
                      {envBadge && (
                        <span className="ml-1.5 px-1 py-0.2 rounded text-[9px] font-semibold tracking-wider uppercase bg-gray-100 text-gray-700 border border-gray-200">
                          {envBadge}
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                {completeness === 'stalled' && (
                  <span
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200 shrink-0"
                    title="The worker is alive but no new record has been saved for 5 minutes. Choose Stop, then Resume remaining. Do not start a new department migration."
                  >
                    <AlertTriangle className="h-3 w-3" />
                    Stalled
                  </span>
                )}
                {completeness === 'paused' && (
                  <span
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200 shrink-0"
                    title="The department migration stopped reporting. Open View report and choose Resume. Do not start a new department migration."
                  >
                    <AlertTriangle className="h-3 w-3" />
                    Paused
                  </span>
                )}
                {completeness === 'in_progress' && (
                  <span
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-50 text-orange-800 border border-orange-200 shrink-0"
                    title="A department migration is still running. Open View report for tab progress. Migrated is hidden until it finishes."
                  >
                    <Loader2 className="h-3 w-3 animate-spin" />
                    In progress
                  </span>
                )}
                {completeness === 'migrated' && (
                  <span
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-50 text-green-700 border border-green-200 shrink-0"
                    title="Every dataset has been run at least once. Open View report to confirm skipped and failed rows are zero."
                  >
                    <Check className="h-3 w-3" />
                    Migrated
                  </span>
                )}
                {completeness === 'partial' && (
                  <span
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200 shrink-0"
                    title="Some datasets migrated (for example Occupancies or Contacts). Remaining datasets are still pending."
                  >
                    Partial
                  </span>
                )}
              </button>
            );
          })}

          {processedBrokenLink.length > 0 && (
            <div className="mt-2 pt-2 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
              <p className="px-3 text-xs font-semibold uppercase tracking-wide opacity-40 mb-1">Broken Link</p>
              {processedBrokenLink.map((entry) => (
                <div
                  key={entry._id}
                  title="Legacy linkage incomplete — re-run onboarding linkage step"
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm opacity-40 cursor-not-allowed"
                >
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
                  <div className="min-w-0">
                    <p className="font-medium truncate">{entry.name}</p>
                    {entry.legacyDeptId != null && (
                      <p className="text-xs truncate">ID {entry.legacyDeptId}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {hiddenCount > 0 && (
            <p className="px-3 pt-2 text-xs opacity-40 text-center">
              {hiddenCount} department{hiddenCount > 1 ? 's' : ''} hidden
            </p>
          )}
        </div>
      )}
    </div>
  );
}
